"use client";

import React, { useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/app/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { Switch } from "@/app/components/ui/switch";
import { Badge } from "@/app/components/ui/badge";
import { Loader2, Globe, Shield, Network, MapPin, Languages } from "lucide-react";
import { toast } from "react-hot-toast";

type Mode = "standard" | "bulk" | "origin";

const LANGS = [
  { code: "en", label: "English" },
  { code: "de", label: "German" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "ja", label: "Japanese" },
  { code: "pt-br", label: "Portuguese (BR)" },
  { code: "ru", label: "Russian" },
  { code: "zh", label: "Chinese" },
] as const;

export default function IpLookupPage() {
  const [mode, setMode] = useState<Mode>("standard");
  const [ipOrDomain, setIpOrDomain] = useState("");
  const [bulkList, setBulkList] = useState("");
  const [hostname, setHostname] = useState(true);
  const [security, setSecurity] = useState(false);
  const [language, setLanguage] = useState<string>("en");
  const [fields, setFields] = useState("");
  const [output, setOutput] = useState<"json" | "xml">("json");
  const [loading, setLoading] = useState(false);
  const [raw, setRaw] = useState<string>("");
  const [parsed, setParsed] = useState<any>(null);

  const query = useMemo(() => {
    if (mode === "standard") return ipOrDomain.trim();
    if (mode === "bulk") {
      const compact = bulkList
        .split(/[,\s]+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .join(",");
      return compact;
    }
    return ""; // origin
  }, [mode, ipOrDomain, bulkList]);

  async function runLookup() {
    setLoading(true);
    setRaw("");
    setParsed(null);

    try {
      const res = await fetch("/api/ip-lookup", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: output === "xml" ? "application/xml" : "application/json" },
        body: JSON.stringify({
          mode,
          query,
          options: { hostname, security, language, fields, output },
        }),
      });

      const text = await res.text();
      setRaw(text);

      if (!res.ok) {
        // Try to extract an error message from JSON
        try {
          const maybe = JSON.parse(text);
          const msg = maybe?.error?.info || maybe?.error || maybe?.message || "Lookup error";
          toast.error(String(msg));
        } catch {
          toast.error(`Lookup failed (${res.status})`);
        }
        return;
      }

      if (output === "json") {
        try {
          const json = JSON.parse(text);
          setParsed(json);
          toast.success("Lookup complete");
        } catch {
          toast("Received non-JSON response", { icon: "😬" });
        }
      } else {
        toast.success("Lookup complete");
      }
    } catch (err: any) {
      toast.error(err?.message ?? "Network error");
    } finally {
      setLoading(false);
    }
  }

  const hasLocation =
    parsed && !Array.isArray(parsed) && (parsed.latitude !== undefined || parsed?.location?.country_flag);

  return (
    <div className="container mx-auto px-4 py-8 animate-fade-in">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="text-center">
          <h1 className="text-4xl font-bold">IP Lookup</h1>
          <p className="text-muted-foreground text-lg mt-2">
            Geolocate IPs, see ISP/ASN, time zone, currency and optional security signals — via a server-side proxy.
          </p>
        </div>

        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Lookup
            </CardTitle>
            <CardDescription>Standard, Bulk, or Origin (“check”). Hostname/security are optional.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Tabs
              value={mode}
              onValueChange={(v) => setMode(v as Mode)}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="standard">Standard</TabsTrigger>
                <TabsTrigger value="bulk">Bulk</TabsTrigger>
                <TabsTrigger value="origin">Origin</TabsTrigger>
              </TabsList>

              {/* STANDARD */}
              <TabsContent value="standard" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ip">IP address or domain</Label>
                  <Input
                    id="ip"
                    placeholder="e.g. 161.185.160.93 or example.com"
                    value={ipOrDomain}
                    onChange={(e) => setIpOrDomain(e.target.value)}
                  />
                </div>
              </TabsContent>

              {/* BULK */}
              <TabsContent value="bulk" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="bulk">Comma/space separated IPs or domains (up to 50)</Label>
                  <textarea
                    id="bulk"
                    rows={4}
                    className="w-full rounded-md border bg-background p-3 text-sm"
                    placeholder="161.185.160.93, 87.111.168.248, 210.138.184.59"
                    value={bulkList}
                    onChange={(e) => setBulkList(e.target.value)}
                  />
                </div>
              </TabsContent>

              {/* ORIGIN */}
              <TabsContent value="origin">
                <p className="text-sm text-muted-foreground">
                  No input needed. We’ll look up the IP your request comes from.
                </p>
              </TabsContent>
            </Tabs>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-md border p-3">
                  <div className="flex items-center gap-2">
                    <Network className="h-4 w-4" />
                    <div>
                      <div className="font-medium">Hostname lookup</div>
                      <div className="text-xs text-muted-foreground">Adds reverse DNS to response</div>
                    </div>
                  </div>
                  <Switch checked={hostname} onCheckedChange={setHostname} />
                </div>

                <div className="flex items-center justify-between rounded-md border p-3">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    <div>
                      <div className="font-medium">Security data</div>
                      <div className="text-xs text-muted-foreground">TOR/proxy/crawler/threat signals</div>
                    </div>
                  </div>
                  <Switch checked={security} onCheckedChange={setSecurity} />
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Languages className="h-4 w-4" />Response language</Label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger>
                      <SelectValue placeholder="Language" />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGS.map((l) => (
                        <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Fields filter (optional)</Label>
                  <Input
                    placeholder="e.g. latitude,longitude or location"
                    value={fields}
                    onChange={(e) => setFields(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Reduce payload. Examples: <code>main</code>, <code>location</code>, <code>latitude,longitude</code>
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Output</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={output === "json" ? "default" : "outline"}
                      onClick={() => setOutput("json")}
                    >
                      JSON
                    </Button>
                    <Button
                      type="button"
                      variant={output === "xml" ? "default" : "outline"}
                      onClick={() => setOutput("xml")}
                    >
                      XML
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                className="btn-enhanced hover-lift"
                disabled={loading || (mode === "standard" && !ipOrDomain) || (mode === "bulk" && !bulkList.trim())}
                onClick={runLookup}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Run Lookup
              </Button>
              {mode === "standard" && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setIpOrDomain("check");
                    setMode("origin");
                  }}
                >
                  Use My IP
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Highlights (when JSON and single-object) */}
        {output === "json" && parsed && !Array.isArray(parsed) && (
          <Card className="card-enhanced">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Quick Facts
              </CardTitle>
              <CardDescription>Core details pulled from the response.</CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">IP</div>
                <div className="font-medium">{parsed.ip ?? "—"}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Type</div>
                <div className="font-medium">{parsed.type ?? "—"}</div>
              </div>
              <div>
                <div className="text-muted-foreground">ISP / ASN</div>
                <div className="font-medium">
                  {parsed?.connection?.isp ?? "—"}{" "}
                  {parsed?.connection?.asn ? <Badge variant="outline">AS{parsed.connection.asn}</Badge> : null}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Country</div>
                <div className="font-medium flex items-center gap-2">
                  {parsed?.location?.country_flag ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={parsed.location.country_flag} alt="" className="h-4 w-6 rounded-sm border" />
                  ) : null}
                  {parsed.country_name ?? "—"}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Region / City</div>
                <div className="font-medium">
                  {parsed.region_name ?? "—"} {parsed.city ? `• ${parsed.city}` : ""}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Coords</div>
                <div className="font-medium">
                  {parsed.latitude != null && parsed.longitude != null
                    ? `${parsed.latitude}, ${parsed.longitude}`
                    : "—"}{" "}
                  {parsed.latitude != null && parsed.longitude != null && (
                    <a
                      className="ml-2 underline text-primary"
                      href={`https://maps.google.com/?q=${parsed.latitude},${parsed.longitude}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View Map
                    </a>
                  )}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Time Zone</div>
                <div className="font-medium">{parsed?.time_zone?.id ?? "—"}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Currency</div>
                <div className="font-medium">
                  {parsed?.currency?.code ? `${parsed.currency.code} (${parsed.currency.symbol ?? ""})` : "—"}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Threat Level</div>
                <div className="font-medium">
                  {parsed?.security?.threat_level ? (
                    <Badge variant={parsed.security.threat_level === "low" ? "default" : "destructive"}>
                      {String(parsed.security.threat_level)}
                    </Badge>
                  ) : (
                    "—"
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Raw response */}
        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle>Raw Response</CardTitle>
            <CardDescription>Exactly what came back from ipapi (via your proxy).</CardDescription>
          </CardHeader>
          <CardContent>
            <pre className="max-h-[480px] overflow-auto rounded-md border p-3 text-sm">
{raw ? raw : "— No response yet —"}
            </pre>
          </CardContent>
        </Card>

        {/* Dev tip */}
        <p className="text-xs text-muted-foreground">
          Heads-up: the proxy route prevents your <code>IPAPI_KEY</code> from leaking to the client. If you ever fetch
          ipapi directly from the browser, your key will be exposed. Keep it server-side and rotate keys if needed.
        </p>
      </div>
    </div>
  );
}
